import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AdminSettingsHeader } from "@/components/admin/settings/AdminSettingsHeader";
import { AdminSettingsNav } from "@/components/admin/settings/AdminSettingsNav";
import { useAdminSettingsStatus } from "@/components/admin/settings/useAdminSettingsStatus";
import { BillingSection } from "@/components/admin/settings/billing/BillingSection";
import { CommunicationSection } from "@/components/admin/settings/communication/CommunicationSection";
import { TherapistOnboardingSection } from "@/components/admin/settings/onboarding/TherapistOnboardingSection";
import { PlatformSection } from "@/components/admin/settings/platform/PlatformSection";
import { PricingSection } from "@/components/admin/settings/pricing/PricingSection";
import {
  parseAdminSettingsSearch,
  type AdminSettingsSection,
  type AdminSettingsTab,
} from "@/components/admin/settings/types";

export const Route = createFileRoute("/dashboard/admin/settings")({
  validateSearch: (search: Record<string, unknown>) => parseAdminSettingsSearch(search),
  component: AdminSettings,
});

function AdminSettings() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/dashboard/admin/settings" });
  const { status } = useAdminSettingsStatus();

  const setSection = (section: AdminSettingsSection) => {
    navigate({ search: section === "platform" ? {} : { section } });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      <AdminSettingsHeader status={status} />

      <div className="grid gap-6 lg:grid-cols-[286px_minmax(0,1fr)]">
        <AdminSettingsNav activeSection={search.section} onSection={setSection} />
        <main className="min-w-0">
          <ActiveSection section={search.section} tab={search.tab} />
        </main>
      </div>
    </div>
  );
}

function ActiveSection({
  section,
  tab,
}: {
  section: AdminSettingsSection;
  tab?: AdminSettingsTab;
}) {
  if (section === "pricing") return <PricingSection />;
  if (section === "billing") return <BillingSection />;
  if (section === "therapist-onboarding") return <TherapistOnboardingSection tab={tab} />;
  if (section === "communication") return <CommunicationSection />;
  return <PlatformSection />;
}
